import { useCallback, useMemo } from "react";
import {
  analysesWithPreviewData,
  extractIPfromNetworkThreatReports,
  getDomainFromUrl,
  getRelevantReportError,
  removeSurroundingQuotes
} from "../util";
import useNetworkThreatIntelDomainReport from "./useNetworkThreatIntelDomainReport";
import useNetworkThreatIntelIpReport from "./useNetworkThreatIntelIPReport";
import useNetworkThreatIntelURLReport from "./useNetworkThreatIntelURLReport";

const getDomain = (
  url: string,
  domainParam?: string,
  domainFromUrlReport?: string
) => {
  if (domainParam) return domainParam;
  if (domainFromUrlReport) return domainFromUrlReport;
  return getDomainFromUrl(url);
};

const useNetworkThreatIntelAllReports = (
  _url: string,
  _domain: string,
  _ip: string
) => {
  const {
    data: urlReport,
    isSuccess: isUrlSuccess,
    isLoading: isLoadingUrlReport,
    error: errorUrlReport,
    isError: isErrorUrlReport,
    isFetching,
    refetch: refetchUrlReport
  } = useNetworkThreatIntelURLReport(_url);

  const hasScreenshotsAndDomData = useMemo(
    () =>
      analysesWithPreviewData(urlReport?.analysis?.analysis_history)
        .hasPreviewData,
    [urlReport]
  );

  const domain = getDomain(
    _url,
    _domain,
    urlReport?.analysis?.last_analysis.domain
  );

  const {
    data: domainReport,
    isSuccess: isDomainSuccess,
    isLoading: isLoadingDomainReport,
    error: domainReportError,
    refetch: refetchDomainReport
  } = useNetworkThreatIntelDomainReport(domain);

  const ipFromReports = extractIPfromNetworkThreatReports({
    urlReport,
    domainReport
  });

  const ip = removeSurroundingQuotes(_ip) || ipFromReports;

  const {
    data: ipReport,
    isSuccess: isIpSuccess,
    isLoading: isLoadingIpReport,
    error: ipReportError,
    refetch: refetchIpReport
  } = useNetworkThreatIntelIpReport(ip);

  const relevantReportError = getRelevantReportError(_url, _domain, _ip, {
    url: errorUrlReport,
    domain: domainReportError,
    ip: ipReportError
  });

  const hasRelevantReportError = Boolean(relevantReportError);

  const refetchAllReports = useCallback(() => {
    refetchUrlReport({ cancelRefetch: true });
    refetchDomainReport({ cancelRefetch: true });
    if (ip) {
      refetchIpReport({ cancelRefetch: true });
    }
  }, [ip, refetchIpReport, refetchUrlReport, refetchDomainReport]);

  return {
    urlData: {
      report: urlReport,
      isSuccess: isUrlSuccess,
      isFetching,
      isLoadingUrlReport,
      errorUrlReport,
      isErrorUrlReport,
      hasScreenshotsAndDomData
    },
    domainData: {
      report: domainReport,
      isSuccess: isDomainSuccess,
      errorDomainReport: domainReportError,
      isError: Boolean(domainReportError),
      isLoadingDomainReport
    },
    ipData: {
      report: ipReport,
      isSuccess: isIpSuccess,
      isLoadingIpReport,
      errorIpReport: ipReportError,
      isError: Boolean(ipReportError)
    },
    relevantReportError,
    hasRelevantReportError,
    refetch: {
      refetchAllReports
    }
  };
};
export default useNetworkThreatIntelAllReports;
